/**
 * Hyperion's display and scheduling timezone default.
 *
 * Blake, 2026-10-05: wherever a tenant, user or schedule has no zone of its
 * own, Hyperion uses Australia/Melbourne. Instants from the API stay UTC;
 * this module only decides which zone they are shown in. Mirrors the
 * backend's pkg/timezone.Default.
 */

export const DEFAULT_TIME_ZONE = 'Australia/Melbourne';

/** True when `zone` is a non-empty IANA zone this browser can format in. */
export function isValidTimeZone(zone: unknown): zone is string {
  if (typeof zone !== 'string' || zone.trim() === '') return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone.trim() });
    return true;
  } catch {
    return false;
  }
}

/** `zone` when valid, otherwise the Melbourne default. */
export function resolveTimeZone(zone?: string | null): string {
  return isValidTimeZone(zone) ? zone.trim() : DEFAULT_TIME_ZONE;
}

let displayTimeZone = DEFAULT_TIME_ZONE;

/**
 * Set the zone the shared formatters display in (the workspace timezone once
 * it is known). Invalid or empty input falls back to the default.
 */
export function setDisplayTimeZone(zone?: string | null): void {
  displayTimeZone = resolveTimeZone(zone);
}

/** The zone shared formatters currently display in. */
export function getDisplayTimeZone(): string {
  return displayTimeZone;
}

/** Workspace date pattern from /settings/general (date_format). */
export type DisplayDateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
/** Workspace clock from /settings/general (time_format). */
export type DisplayTimeFormat = '12h' | '24h';

export interface DisplayFormats {
  /** null until a workspace has saved one: shared formatters then use "Jan 15, 2026". */
  dateFormat: DisplayDateFormat | null;
  timeFormat: DisplayTimeFormat;
}

const DATE_FORMATS: readonly DisplayDateFormat[] = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];
const TIME_FORMATS: readonly DisplayTimeFormat[] = ['12h', '24h'];
const CACHE_KEY = 'hyperion.display-preferences';

let displayFormats: DisplayFormats = { dateFormat: null, timeFormat: '12h' };

function asDateFormat(v: unknown): DisplayDateFormat | null {
  return DATE_FORMATS.includes(v as DisplayDateFormat) ? (v as DisplayDateFormat) : null;
}

function asTimeFormat(v: unknown): DisplayTimeFormat {
  return TIME_FORMATS.includes(v as DisplayTimeFormat) ? (v as DisplayTimeFormat) : '12h';
}

/** Set the date/time patterns the shared formatters use. Unknown values reset to the defaults. */
export function setDisplayFormats(formats?: { dateFormat?: unknown; timeFormat?: unknown } | null): void {
  displayFormats = {
    dateFormat: asDateFormat(formats?.dateFormat),
    timeFormat: asTimeFormat(formats?.timeFormat),
  };
}

/** The date/time patterns shared formatters currently use. */
export function getDisplayFormats(): DisplayFormats {
  return displayFormats;
}

/** The subset of the /settings/general response the display layer needs. */
export interface WorkspaceDisplayPreferences {
  configured?: boolean;
  timezone?: string | null;
  dateFormat?: unknown;
  timeFormat?: unknown;
}

/**
 * Apply the workspace's display preferences (zone + formats) to every shared
 * formatter, and remember them so the next page load starts in the right zone
 * before /settings/general answers. An unconfigured workspace resets to the
 * Melbourne / "Jan 15, 2026" / 12h defaults.
 */
export function applyWorkspaceDisplayPreferences(prefs?: WorkspaceDisplayPreferences | null): void {
  if (prefs?.configured) {
    setDisplayTimeZone(prefs.timezone);
    setDisplayFormats(prefs);
  } else {
    setDisplayTimeZone(undefined);
    setDisplayFormats(undefined);
  }
  try {
    globalThis.localStorage?.setItem(
      CACHE_KEY,
      JSON.stringify({ timeZone: displayTimeZone, ...displayFormats })
    );
  } catch {
    // Storage blocked: the defaults still render correctly.
  }
}

/** Restore the last applied preferences (called once at module load). */
function restoreCachedPreferences(): void {
  try {
    const raw = globalThis.localStorage?.getItem(CACHE_KEY);
    if (!raw) return;
    const cached = JSON.parse(raw) as { timeZone?: string; dateFormat?: unknown; timeFormat?: unknown };
    setDisplayTimeZone(cached.timeZone);
    setDisplayFormats(cached);
  } catch {
    // Corrupt or blocked storage: keep the defaults.
  }
}

// Tests start from the documented defaults, not whatever a previous run cached.
if (import.meta.env.MODE !== 'test') restoreCachedPreferences();
